import { Sparkle, Star } from "lucide-react";
import { memo } from "react";

import { PALETTE } from "../constants";

interface OrbitParticlesProps {
  enabled: boolean;
}

type ParticleShape = "sparkle" | "star" | "dot";

interface Particle {
  shape: ParticleShape;
  /** Orbit keyframe name (sets radius + direction). */
  orbit: string;
  orbitDur: string;
  twinkleDur: string;
  twinkleDelay: string;
  sizeClass: string;
  color: string;
  glow: string;
}

const PARTICLES: Particle[] = [
  { shape: "sparkle", orbit: "maya-orbit-1", orbitDur: "4.5s", twinkleDur: "1.6s", twinkleDelay: "0s", sizeClass: "h-2.5 w-2.5", color: "var(--color-primary)", glow: "drop-shadow-[0_0_4px_rgba(124,92,252,0.9)]" },
  { shape: "star", orbit: "maya-orbit-2", orbitDur: "6s", twinkleDur: "2.1s", twinkleDelay: "0.4s", sizeClass: "h-2 w-2", color: "var(--color-primary)", glow: "drop-shadow-[0_0_4px_rgba(139,104,232,0.9)]" },
  { shape: "sparkle", orbit: "maya-orbit-3", orbitDur: "5.2s", twinkleDur: "1.8s", twinkleDelay: "0.8s", sizeClass: "h-1.5 w-1.5", color: "var(--color-primary)", glow: "drop-shadow-[0_0_3px_rgba(227,215,255,0.9)]" },
  { shape: "star", orbit: "maya-orbit-4", orbitDur: "6.8s", twinkleDur: "2.3s", twinkleDelay: "1.1s", sizeClass: "h-2 w-2", color: "var(--color-danger)", glow: "drop-shadow-[0_0_4px_rgba(244,114,182,0.9)]" },
  { shape: "sparkle", orbit: "maya-orbit-5", orbitDur: "5.6s", twinkleDur: "1.9s", twinkleDelay: "0.6s", sizeClass: "h-1.5 w-1.5", color: "var(--color-danger)", glow: "drop-shadow-[0_0_3px_rgba(244,114,182,0.85)]" },
  { shape: "star", orbit: "maya-orbit-6", orbitDur: "7.4s", twinkleDur: "2.5s", twinkleDelay: "1.4s", sizeClass: "h-2.5 w-2.5", color: "var(--color-primary)", glow: "drop-shadow-[0_0_4px_rgba(124,92,252,0.85)]" },
  { shape: "dot", orbit: "maya-orbit-rev", orbitDur: "7s", twinkleDur: "1.4s", twinkleDelay: "0.2s", sizeClass: "h-1 w-1", color: "var(--color-primary)", glow: "" },
];

const ParticleGlyph = ({ p }: { p: Particle }) => {
  const common = {
    className: `maya-animated ${p.sizeClass} ${p.glow}`,
    style: {
      animation: `maya-twinkle ${p.twinkleDur} ease-in-out infinite`,
      animationDelay: p.twinkleDelay,
      color: p.color,
    },
    fill: "currentColor",
  } as const;

  if (p.shape === "dot") {
    return (
      <span
        className={`maya-animated block ${p.sizeClass} rounded-full`}
        style={{
          animation: `maya-twinkle ${p.twinkleDur} ease-in-out infinite`,
          animationDelay: p.twinkleDelay,
          background: p.color,
          boxShadow: "0 0 5px 2px rgba(124,92,252,0.7)",
        }}
      />
    );
  }
  return p.shape === "star" ? <Star {...common} /> : <Sparkle {...common} />;
};

export const OrbitParticles = memo(({ enabled }: OrbitParticlesProps) => {
  if (!enabled) return null;

  return (
    <>
      <span
        aria-hidden="true"
        className="maya-animated pointer-events-none absolute inset-0 m-auto h-12 w-12 sm:h-14 sm:w-14 rounded-full blur-md"
        style={{
          background: PALETTE.auraGlow,
          animation: "maya-glow-pulse 2.4s ease-in-out infinite",
        }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="maya-animated absolute"
            style={{
              animation: `${p.orbit} ${p.orbitDur} linear infinite`,
              transformOrigin: "center",
            }}
          >
            <ParticleGlyph p={p} />
          </span>
        ))}
      </span>
    </>
  );
});

OrbitParticles.displayName = "OrbitParticles";
